import type {ReactElement} from 'react';
import React, {useContext} from 'react';
import {SocialProfileIcon, SocialProfileIconSize} from '@Components/social-media/components/social-profile-icon';
import type {SocialAccount} from '@Components/social-media/account.vo';
import {getSocialPlatformDetailedName} from '@Libraries/social-media-platform.library';
import {SocialMediaWizardStep} from '@Components/social-media-wizard/social-media-wizard.types';
import {useSocialPostPublishingAction, useWizardStep} from '@Components/social-media/social-media.hooks';
import {SocialMediaWizardContainerQueriesContext} from '@Components/social-media-wizard/social-media-wizard-container-queries-context';
import {Icon} from '@Components/icon-v2';
import {FlatIconColor, FlatIconColorType, IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {PlatformMediaType} from '@Components/social-media/post.vo';
import styles from './social-account-label.module.scss';
import {getUserId} from '@Libraries/user.library';
import {Text, TextSize} from '@Components/text';
import Tooltip from 'react-bootstrap/Tooltip';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import {SocialAccountRemoveMenu} from './components/social-account-remove-menu/social-account-remove-menu';
import {clickOnKeyboardActivation} from '@Utils/dom.util';
import {SUBTITLE_SUFFIX_SEPARATOR} from './social-account-label.types';

interface SocialAccountLabelProps {
  account: SocialAccount;
  showAccountDetails?: boolean;
  isDisabled?: boolean;
  className?: string;
  isConnectionExpired?: boolean;
  isSelected?: boolean;
  profileIconSize?: SocialProfileIconSize;
  accountNameClasses?: string;
  platformMediaType?: PlatformMediaType;
  onRemoveAccountClick?: VoidFunction;
  subtitleOverride?: string;
  subtitleSuffix?: string;
  subtitleClassName?: string;
}

function SocialAccountLabel({
  account,
  showAccountDetails = false,
  isDisabled = false,
  className = '',
  isConnectionExpired = false,
  isSelected = false,
  profileIconSize,
  accountNameClasses = '',
  platformMediaType = PlatformMediaType.POST,
  onRemoveAccountClick,
  subtitleClassName = '',
  subtitleOverride,
  subtitleSuffix = '',
}: SocialAccountLabelProps): ReactElement {
  const wizardStep = useWizardStep();
  const {isSmallScreen, isExtraSmallScreen} = useContext(SocialMediaWizardContainerQueriesContext);
  const publishingAction = useSocialPostPublishingAction();

  const isInEditingState = (): boolean => {
    return wizardStep === SocialMediaWizardStep.ADD_DETAILS && publishingAction === undefined;
  };

  const getRemoveAccountCTA = (): ReactElement | null => {
    if (!onRemoveAccountClick || !isInEditingState()) {
      return null;
    }

    if (isSmallScreen || isExtraSmallScreen) {
      return <SocialAccountRemoveMenu onRemoveAccount={onRemoveAccountClick} />;
    }

    return (
      <Icon
        icon="icon-delete"
        className={`content-danger ${styles.removeAccountCta}`}
        size={IconSize.SIZE_ICON_16}
        shape={IconShape.SQUARE}
        onClick={onRemoveAccountClick}
        onKeyDown={clickOnKeyboardActivation}
        role="button"
        tabIndex={0}
        isUsedAsButton
        ariaLabel={window.i18next.t('pmwjs_remove_account')}
      />
    );
  };

  const getAccountNameTextElement = (): ReactElement => {
    return (
      <div className={styles.accountNameStack}>
        <div className="flex-h-row align-items-center gap-2">
          <span className={styles.accountNameText} title={account.name}>
            {account.name}
          </span>
          {getSharedInTeamElement()}
        </div>
        {getConnectionStatusElement()}
      </div>
    );
  };

  const getConnectionStatusElement = (): ReactElement | null => {
    if (!isConnectionExpired) {
      return null;
    }

    return <Text size={TextSize.MD} className={styles.connectionStatusText} val={window.i18next.t('pmwjs_connection_expired')} />;
  };

  const getSharedInTeamElement = (): ReactElement | null => {
    if (account.idUser !== getUserId() && account.isSharedInTeam) {
      return (
        <OverlayTrigger
          trigger={['hover', 'focus']}
          placement="top"
          delay={{show: 50, hide: 100}}
          overlay={
            <Tooltip id="tooltip-icon" className="spacing-m-b-3 spacing-m-l-1">
              <Text size={TextSize.XSMALL} val={window.i18next.t('pmwjs_shared_in_team')} />
            </Tooltip>
          }
        >
          {
            <Icon
              className={`flex-shrink-0 ${styles.sharedInTeamBadge}`}
              icon={'icon-team'}
              shape={IconShape.CIRCLE}
              type={IconType.FLAT}
              flatIconType={{color: FlatIconColor.PRIMARY, type: FlatIconColorType.DEFAULT}}
              size={IconSize.SIZE_ICON_12}
            />
          }
        </OverlayTrigger>
      );
    }

    return null;
  };

  const getExpiredAccountTextClasses = (): string => {
    return isConnectionExpired ? styles.hasExpirationText : '';
  };

  const getAccountDetailsNameClasses = (): string => {
    return `${styles.accountNameRow} ${styles.profileName} ${getExpiredAccountTextClasses()} ${getDisabledClass()} ${getSelectedClass()} body-md-bold content-body ${accountNameClasses}`;
  };

  const getSubtitleElement = (): ReactElement => {
    if (subtitleOverride) {
      return <Text size={TextSize.MD} className={styles.errorSubtitle} val={subtitleOverride} />;
    }

    return <Text size={TextSize.MD} className={`${styles.accountSubtitle} ${getDisabledClass()} ${subtitleClassName}`} val={getSubtitleText()} />;
  };

  const getSubtitleText = (): string => {
    const platformName = getSocialPlatformDetailedName(account.type);

    return subtitleSuffix === '' ? platformName : `${platformName} ${SUBTITLE_SUFFIX_SEPARATOR} ${subtitleSuffix}`;
  };

  const getSocialMediaAccountLabels = (): ReactElement => {
    if (!showAccountDetails) {
      return (
        <span
          className={`${styles.profileName} ${getExpiredAccountTextClasses()} ${getDisabledClass()} ${getSelectedClass()} flex-vertical-center body-md-regular content-body spacing-m-l-3`}
        >
          {getAccountNameTextElement()}
        </span>
      );
    }

    return (
      <div className={`flex-v-row ${styles.accountDetailsStack}`}>
        <span className={getAccountDetailsNameClasses()}>{getAccountNameTextElement()}</span>
        {getSubtitleElement()}
      </div>
    );
  };

  const getDisabledClass = (): string => {
    return isDisabled ? styles.disabled : '';
  };

  const getSelectedClass = (): string => {
    return isSelected ? styles.selected : '';
  };

  const getProfileIconSize = (): SocialProfileIconSize => {
    if (profileIconSize) {
      return profileIconSize;
    }

    if (showAccountDetails) {
      return SocialProfileIconSize.LARGE;
    }

    return SocialProfileIconSize.MEDIUM;
  };

  return (
    <div className={`flex-h-row flex-items-center flex-justify-between ${styles.accountLabelRoot} ${className}`}>
      <div className={`flex-h-row flex-items-center ${styles.accountLabelContent}`}>
        <SocialProfileIcon platformMediaType={platformMediaType} account={account} isDisabled={isDisabled} size={getProfileIconSize()} isError={isConnectionExpired} />
        {getSocialMediaAccountLabels()}
      </div>
      {getRemoveAccountCTA()}
    </div>
  );
}

export default React.memo(SocialAccountLabel);
